/* Angular Component */
import { Component } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { ExternalMAMGatewayProvider } from "app/js/src/domain/external-mam/external-mam-gateway-provider";
import { IExternalMAMRepository } from "app/js/src/domain/external-mam/i-external-mam.repository";
import { Subject } from "rxjs/internal/Subject";
import {
  ColDef,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
} from "ag-grid-community";
import { FilterBlock, FilterTerm } from "../../../domain/visual-filters";
import { ExternalMAMSearchRequest } from "app/js/src/domain/external-mam/external-mam-search-request";
import { ExternalMAMSearchResultsNameCell } from "./search-grid-components/external-mam-search-results-name-cell/external-mam-search-results-name-cell.component";
import moment from "moment";
import { ExternalMAMSearchResultsStatusCell } from "./search-grid-components/external-mam-search-results-status-cell/external-mam-search-results-status-cell.component";
import { ExternalMAMServiceProvider } from "app/js/src/domain/external-mam/external-mam-service-provider";
import { ExternalMAMImportRequest } from "app/js/src/domain/external-mam/external-mam-import-request";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { Socket } from "app/js/src/infrastructure/socket.service";
import { ContentUploadStatus } from "app/js/src/domain/media";
import { ExternalMAMSearchResultsActionsCell } from "./search-grid-components/external-mam-search-results-actions-cell/external-mam-search-results-actions-cell.component";
import { customizeColumnDef } from "./external-mam-customize-table/external-mam-customize-table.component";
import { AgGridEvrideoCheckboxCellComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-checkbox-cell/ag-grid-evrideo-checkbox-cell.component";
import { ExternalMAMUpdateItemMetadataRequest } from "app/js/src/domain/external-mam/external-mam-update-item-metadata-request";
import { AgGridEvrideoSelectAllHeaderColumnComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-select-all-header-column/ag-grid-evrideo-select-all-header-column.component";
import { AgGridEvrideoCustomLoadingOverlayComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-custom-loading-overlay/ag-grid-evrideo-custom-loading-overlay.component";
import { AgGridEvrideoVideoNameCellComponent } from "../../common/ag-grid-evrideo/ag-grid-evrideo-video-name-cell/ag-grid-evrideo-video-name-cell.component";

@Component({
  selector: "external-mam-gateway-playground",
  templateUrl: "./external-mam-gateway-playground.component.html",
  styleUrls: ["./external-mam-gateway-playground.component.scss"],
})
export class ExternalMAMGatewayPlaygroundComponent {
  private uploadFinishedChangeSubscription: Subscription;
  gatewayProvider: ExternalMAMGatewayProvider = null;
  serviceProvider: ExternalMAMServiceProvider = null;
  clearSearchSubject: Subject<void> = new Subject<void>();
  searchParams: FilterBlock[];
  searchTerms: Array<FilterTerm> = [];
  gridApi: GridApi;
  gridColumnApi: any;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  public pageSize = 100;
  overlayNoRowsTemplate =
    "<span style=\"padding: 10px; border: 2px solid #666; background: #55AA77;\">This is a custom 'no rows' overlay</span>";
  localTimezone: string;
  selectedTableItemsCount: number = 0;
  connectionId;
  showCustomizeTable: boolean = false;
  columnsForCostumization: Array<customizeColumnDef> = [];
  dataSource: IDatasource = {
    rowCount: undefined,
    getRows: async (params: IGetRowsParams) => {
      // At this point in your code, you would call the server.
      // To make the demo look real, wait for 500ms before returning
      try {
        let request = new ExternalMAMSearchRequest(
          this.gatewayProvider.id,
          this.searchParams,
        );
        if (params.sortModel.length > 0) {
          var sortParam = params.sortModel[0];
          request.searchOperators.addFieldToOrder(
            sortParam.colId,
            sortParam.sort === "desc",
          );
        }
        request.searchOperators.limit = params.context.componentParent.pageSize;
        request.searchOperators.offset = params.startRow;
        params.context.componentParent.manuallyShowLoadingOverlay();
        let [count, results] = await this._repository.getSearchResults(request);
        params.successCallback(results, count);
        //params.successCallback([], 1);
      } catch {
      } finally {
        params.context.componentParent.manuallyHideOverlay();
        if (params?.context?.componentParent?.gridApi) {
          params?.context?.componentParent?.gridApi?.refreshCells({
            force: true,
          });
        }
      }
    },
  };

  constructor(
    private _repository: IExternalMAMRepository,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private socketService: Socket,
  ) {}

  async ngOnInit() {
    this.connectionId = this.socketService.connectionId;
    this.localTimezone = moment.tz.guess();
    this.route.params.subscribe(async (params: Params) => {
      if (!!params["id"]) {
        const [provider, metadata] =
          await this._repository.getGatewayProviderById(params["id"]);
        if (provider === null) {
          this.router.navigate(["/external-mam"]);
        }
        this.serviceProvider = await this._repository.getServiceProviderById(
          provider.externalMAMType,
        );
        this.searchTerms = [
          ...metadata.map((x) => {
            return {
              displayName: x.displayName,
              id: x.id,
              options: x.availableOptions,
            } as FilterTerm;
          }),
        ];
        let columnDefs = this.getRowDefinitionsByServiceProvider(
          provider.externalMAMType,
        );
        this.gridOptions = <GridOptions>{
          context: { componentParent: this },
          rowModelType: "infinite",
          columnDefs: columnDefs,
          defaultColDef: {
            resizable: true,
            lockPosition: true,
            sortable: false,
            width: 155,
            flex: 1,
            cellClass: "external-mam-search-results-default-cell-class",
          } as ColDef,
          rowHeight: 44,
          suppressHorizontalScroll: true,
          suppressRowClickSelection: true,
          animateRows: true,
          paginationAutoPageSize: false,
          paginationPageSize: this.pageSize,
          pagination: true,
          enableCellTextSelection: true,
          rowSelection: "multiple",
          getRowHeight: (e) => {
            let height = 44;
            return height;
          },
          datasource: this.dataSource,
          maxConcurrentDatasourceRequests: 2,
          loadingOverlayComponent: AgGridEvrideoCustomLoadingOverlayComponent,
          loadingOverlayComponentParams: {
            loadingMessage:
              "data:image/svg+xml;base64," + this.serviceProvider.logo,
          },
        };
        this.gatewayProvider = provider;
      }
    });
    this.uploadFinishedChangeSubscription =
      this.socketService.uploadFinishedChange.subscribe((result) => {
        if (
          result.contentUploadStatus == ContentUploadStatus.Enabled &&
          this.gridApi
        ) {
          setTimeout(() => {
            this.gridApi.forEachNode((node) => {
              if (node.id === result.tenantMediaId) {
                node.data.isEvrideoImported = true;
                this.gridApi.refreshCells({ force: true });
                this.toastr.success(
                  `media id:${result.tenantMediaId} successfully uploaded`,
                );
              }
            });
          });
        }
      });
  }
  ngOnDestroy() {
    if (this.uploadFinishedChangeSubscription) {
      this.uploadFinishedChangeSubscription.unsubscribe();
    }
  }

  importButtonClicked() {
    if (this.gridApi) {
      const nodes = this.gridApi.getSelectedNodes();
      const ids = [];
      nodes.forEach((node) => {
        if (node.data.id) {
          ids.push(node.data.id);
        }
      });
      if (ids.length > 0) {
        this.import(ids);
      }
    }
  }
  clearSearchClicked() {
    this.clearSearchSubject.next();
  }
  setSearchParameters(event) {
    this.searchParams = event;
  }
  doSearch() {
    this.gridApi.setGridOption("datasource", this.dataSource);
    this.manuallyShowLoadingOverlay();
  }
  async import(ids: Array<string>) {
    var request = new ExternalMAMImportRequest(
      this.gatewayProvider.id,
      this.connectionId,
      ids,
    );
    var result = await this._repository.startImport(request);
    if (!result) {
      this.toastr.success("Starting to import videos");
    } else {
      var vids = result.split(",");

      vids.forEach((id) => {
        this.toastr.error(`video id:${id} was already imported`, "Error", {
          closeButton: true,
          tapToDismiss: true,
        });
      });
      if (vids.length != ids.length) {
        this.toastr.success("Starting to import rest of videos");
      }
    }
  }
  async updateItemMetadata(ids: Array<string>) {
    var request = new ExternalMAMUpdateItemMetadataRequest(
      this.gatewayProvider.id,
      ids,
    );
    this.toastr.info("starting to update videos metadata");
    var result = await this._repository.updateItemMetadata(request);
    if (!result) {
      this.toastr.success(`videos metadata was updated`);
    } else {
      var vids = result.split(",");

      vids.forEach((id) => {
        this.toastr.error(`video id:${id} was not updated`, "Error", {
          closeButton: true,
          tapToDismiss: true,
        });
      });
      if (vids.length != ids.length) {
        this.toastr.success("the rest of videos metadata got updated");
      }
    }
  }
  manuallyShowLoadingOverlay() {
    if (this.gridApi) {
      setTimeout(() => this.gridApi.showLoadingOverlay());
    }
  }
  manuallyHideOverlay() {
    setTimeout(() => this.gridApi.hideOverlay());
  }
  onSelectionChanged(event) {
    var nodes = event.api.getSelectedNodes();
    this.selectedTableItemsCount = nodes.length;
  }
  setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    if (this.gridApi) {
      this.doSearch();
    }
  }

  goToPage(page: number) {
    if (this.gridApi) {
      this.gridApi.paginationGoToPage(page);
    }
  }

  get pageCount() {
    let firstRow = 0;
    let lastRow = this.pageSize;
    let finalRow: string | number = "more";
    if (this.gridApi) {
      const pageSize = this.gridApi.paginationGetPageSize();
      const currentPage = this.gridApi.paginationGetCurrentPage();
      firstRow = currentPage * pageSize;
      lastRow = firstRow + pageSize;
      const vc = this.gridApi.getInfiniteRowCount();
      if (vc) {
        finalRow = vc;
      }
      if (currentPage + 1 == this.gridApi.paginationGetTotalPages()) {
        lastRow = vc;
      }
    }
    return `${firstRow + 1}-${parseInt(lastRow.toString(), 10)} of ${finalRow}`;
  }

  toggleCustomizeTable() {
    this.showCustomizeTable = !this.showCustomizeTable;
  }
  onCustomizeTableExit(columns: Array<customizeColumnDef>) {
    this.showCustomizeTable = false;
    if (columns) {
      var visibleCols = columns
        .filter((x) => x.visible)
        .map((x) => x.columnName?.toLowerCase());
      var allColsDefs = this.getRowDefinitionsByServiceProvider(
        this.gatewayProvider.externalMAMType,
      );
      allColsDefs.forEach((colDef: ColDef) => {
        if (colDef.pinned) {
          return;
        }
        if (visibleCols.includes(colDef.headerName.toLowerCase())) {
          colDef.hide = false;
        } else {
          colDef.hide = true;
        }
      });
      const sortedColDefs = allColsDefs.filter((x) => x.pinned);
      columns.forEach((col) => {
        const colName = col.columnName.toLowerCase();
        sortedColDefs.push(
          allColsDefs.find((x) => x.headerName?.toLowerCase() == colName),
        );
      });
      this.gridApi.setGridOption("columnDefs", sortedColDefs);
      this.gridApi.sizeColumnsToFit();
      this.setColumnsForCostumization();
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
    this.manuallyShowLoadingOverlay();
    //this.scheduledHistoricalJobCacheUpdated =      this.recordingService.getRecordingHistoricalJobCacheUpdateObserver().subscribe(this.updateJobsInGrid.bind(this));
    this.afterGridUpdateTrigger();
    this.setColumnsForCostumization();
  }
  afterGridUpdateTrigger() {
    if (!this.gridApi) {
      return;
    }
    //this.sortGridByStartTime();
    //this.validatePagination();
  }
  //   sortGridByStartTime() {
  //     this.gridApi.setSortModel([{
  //       colId: 'startTime',
  //       sort: 'desc'
  //     }]);
  //   }
  setColumnsForCostumization() {
    const result = [];
    if (this.gridApi) {
      this.gridApi.getColumns().forEach((col) => {
        const udcf = col.getUserProvidedColDef();
        if (
          !col.getPinned() &&
          udcf.headerName &&
          result.findIndex(
            (x) => x.columnName.toLowerCase() == udcf.headerName.toLowerCase(),
          ) == -1
        ) {
          let cc = {
            columnName: udcf.headerName,
            visible: col.isVisible(),
          } as customizeColumnDef;
          result.push(cc);
        }
      });
    }
    this.columnsForCostumization = result;
  }
  getRowDefinitionsByServiceProvider(id: number): Array<any> {
    let base = [
      {
        resizable: false,
        pinned: "left",
        maxWidth: 60,

        cellRenderer: AgGridEvrideoCheckboxCellComponent,
        headerComponent: AgGridEvrideoSelectAllHeaderColumnComponent,
        cellStyle: { padding: 0 },
      },
      {
        headerName: "STATUS",
        maxWidth: 86,
        pinned: "right",
        cellRenderer: ExternalMAMSearchResultsStatusCell,
      },
      {
        headerName: "",
        maxWidth: 55,
        pinned: "right",
        cellRenderer: ExternalMAMSearchResultsActionsCell,
      },
    ];
    switch (id) {
      case 1: {
        let standard = [
          ...base,
          ...[
            { field: "id", headerName: "ID" },
            {
              field: "reference_id",
              headerName: "Reference ID",
              sortable: true,
            },
            {
              field: "name",
              headerName: "NAME",
              cellRenderer: AgGridEvrideoVideoNameCellComponent,
              cellRendererParams: {
                videoName: (data) => data?.name || "",
                videoThumb: (data) => data?.images?.thumbnail?.src || "",
              },
            },
            { field: "description", headerName: "DESCRIPTION" },
            {
              field: "long_description",
              headerName: "Long ESCRIPTION",
              hide: true,
            },
            {
              field: "created_at",
              headerName: "CREATED AT",
              cellRenderer: this.formatDateTime.bind(this),
              sortable: true,
            },
            {
              field: "updated_at",
              headerName: "UPDATED AT",
              cellRenderer: this.formatDateTime.bind(this),
              sortable: true,
            },
            {
              field: "duration",
              headerName: "DURATION",
              cellRenderer: this.formatDuration,
            },
            {
              field: "tags",
              headerName: "Tags",
              hide: true,
            },
            {
              field: "schedule.starts_at",
              headerName: "Schedule Starts at",
              hide: true,
            },
            {
              field: "schedule.ends_at",
              headerName: "Schedule Ends at",
              hide: true,
            },
          ],
        ];

        return [
          ...standard,
          ...this.searchTerms
            .filter(
              (x) =>
                (standard as Array<ColDef>).findIndex((z) => z.field == x.id) ==
                -1,
            )
            .map((x) => {
              return {
                headerName: x.displayName,
                hide: true,
                field: `custom_fields.${x.id}`,
              };
            }),
        ];
      }

      case 2: {
        // Dalet provider – columns match Dalet API response (id, name, itemCode, author, lastModifiedTime, assetType, metadata, etc.)
        let daletColumns = [
          ...base,
          { field: "id", headerName: "ID" },
          {
            field: "itemCode",
            headerName: "Item Code",
            sortable: true,
          },
          {
            field: "name",
            headerName: "NAME",
            cellRenderer: AgGridEvrideoVideoNameCellComponent,
            cellRendererParams: {
              videoName: (data) => data?.name || "",
              videoThumb: (data) => data?.thumbnailUrl || "",
            },
          },
          { field: "author", headerName: "AUTHOR" },
          {
            field: "lastModifiedTime",
            headerName: "LAST MODIFIED",
            cellRenderer: this.formatDateTime.bind(this),
            sortable: true,
          },
          { field: "assetType", headerName: "ASSET TYPE" },
          { field: "statusId", headerName: "STATUS ID" },
        ];
        return [
          ...daletColumns,
          ...this.searchTerms
            .filter(
              (x) =>
                (daletColumns as Array<ColDef>).findIndex((z) => z.field === x.id) === -1
            )
            .map((x) => ({
              headerName: x.displayName,
              hide: true,
              field: `metadata.${x.id}`,
            })),
        ];
      }

      default:
        return [];
    }
  }
  formatDateTime(param: any) {
    if (!param.value) {
      return "";
    }
    return moment(param.value).utc().format("Y/MM/DD HH:mm");
  }
  formatDuration(param: any) {
    if (!param.value) {
      return "";
    }
    return moment(param.value).utc().format(`HH:mm:ss.SSS`);
  }
}
